import * as React from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { triggerChannelArticle } from '../../Api';
import { BetupFeedChannelDto } from '../../models/dtos/BetupFeedChannelDto';
import { channelName } from '../betupFeed/BetupFeedChannelDialogs';

/**
 * A manual AI article trigger (docs/channel-articles.md §1): the channel and the match, each fixed by the page that
 * opens it or chosen here (none = picked automatically). Manual triggers skip the scheduler's limits.
 */
export default function WriteArticleDialog({ open, channels, channelId, matchId, onClose, onQueued }: {
    open: boolean;
    /** The channels to choose from (Publishing › Run now, the match page); empty when [channelId] is fixed. */
    channels: BetupFeedChannelDto[];
    /** A fixed channel (the channel page); null = choose one, or none for the scheduler's next channel. */
    channelId: number | null;
    /** A fixed match (the match page); null = type one, or none for the channel's best match. */
    matchId: number | null;
    onClose: () => void;
    onQueued: (channelId: number | null, matchId: number | null) => void;
}) {
    const [channel, setChannel] = React.useState<string>('');
    const [match, setMatch] = React.useState('');
    const [error, setError] = React.useState<string | null>(null);
    const [sending, setSending] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setChannel(channelId != null ? String(channelId) : '');
        setMatch(matchId != null ? String(matchId) : '');
        setError(null);
    }, [open, channelId, matchId]);

    const send = async () => {
        const chosenChannel = channel ? Number(channel) : null;
        const chosenMatch = match.trim() ? Number(match) : null;
        if (matchId != null && chosenChannel == null) {
            setError('Choose the channel that writes about this match.');
            return;
        }
        setSending(true);
        setError(null);
        const result = await triggerChannelArticle(chosenChannel, chosenMatch);
        setSending(false);
        if (result.error) setError(result.error);
        else onQueued(chosenChannel, chosenMatch);
    };

    const fixedChannel = channelId != null;
    const fixedMatch = matchId != null;
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{fixedMatch ? 'Draft an article about this match' : 'Write an article'}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sends one trigger to Crons: the AI researches the match if needed, writes in the channel's voice and
                    languages, and saves a draft (or publishes it, when auto-publish is on). The scheduler's limits don't
                    apply. It waits behind triggers already in the queue.
                </Typography>
                {!fixedChannel && (
                    <TextField select fullWidth size="small" label={fixedMatch ? 'Channel' : 'Channel (optional)'} value={channel}
                        onChange={e => setChannel(e.target.value)} sx={{ mb: 2 }}
                        helperText={fixedMatch ? undefined : 'None: the channel the scheduler would write for next.'}>
                        {!fixedMatch && <MenuItem value="">Picked automatically</MenuItem>}
                        {channels.map(c => (
                            <MenuItem key={c.id} value={String(c.id)}>{channelName(c)}{c.active ? '' : ' (inactive)'}</MenuItem>
                        ))}
                    </TextField>
                )}
                {!fixedMatch && (
                    <TextField fullWidth size="small" label="Match id (optional)" value={match}
                        onChange={e => setMatch(e.target.value.replace(/[^\d]/g, ''))}
                        onKeyDown={e => { if (e.key === 'Enter' && !sending) send(); }}
                        helperText="None: the channel's best upcoming match (its sports, fresh research first, most bets)." />
                )}
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" startIcon={<EditNoteIcon />} disabled={sending} onClick={send}>Write article</Button>
            </DialogActions>
        </Dialog>
    );
}
